/**
 * Credit notes — money owed back to dealers from returns / write-offs /
 * goodwill discounts.
 *
 * Page composition:
 *   - PageHeader + "New credit note" action
 *   - KPI strip (Total issued, Remaining, Applied)
 *   - DataTable
 *   - Detail Sheet on row click
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Receipt } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  type StatusPillVariant,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  CreditNoteForm,
  type CreditNoteFormValues,
} from "../../../components/forms/CreditNoteForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockCreditNotes,
  mockInvoices,
  type MockCreditNote,
  type MockInvoice,
} from "../../../lib/invoicingMock";
import { useAppSettings } from "../../../lib/appSettings";
import { generateNumber, DEFAULT_PREFIXES } from "../../../lib/invoiceNumbering";

import { mockDealers, type MockDealer } from "../../../lib/salesMock";

type MockDealerRecord = MockDealer;

export const Route = createFileRoute("/_app/sales/credit-notes")({
  component: CreditNotesPage,
});

const STATUS_VARIANTS: Record<MockCreditNote["status"], StatusPillVariant> = {
  draft: "neutral",
  issued: "info",
  applied: "success",
  void: "neutral",
};

const STATUS_LABELS: Record<MockCreditNote["status"], string> = {
  draft: "Draft",
  issued: "Issued",
  applied: "Applied",
  void: "Void",
};

const REASON_LABELS: Record<string, string> = {
  return: "Return",
  discount: "Discount",
  adjustment: "Adjustment",
  writeoff: "Write-off",
};

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function CreditNotesPage() {
  const isMock = useMockData();
  const { companyProfile } = useAppSettings();
  const [rows, setRows] = usePersistentMockState<MockCreditNote[]>("creditNotes", mockCreditNotes);
  const [invoices] = usePersistentMockState<MockInvoice[]>(
    "invoices",
    mockInvoices,
  );
  const [dealers] = usePersistentMockState<MockDealerRecord[]>(
    "dealers",
    mockDealers,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockCreditNote | null>(null);

  /* KPIs */
  const kpis = useMemo(() => {
    let issued = 0n;
    let remaining = 0n;
    let applied = 0n;
    for (const c of rows) {
      if (c.status === "void") continue;
      issued += c.amountInBaseCents;
      remaining += c.remainingAmountInBaseCents;
      applied += c.amountInBaseCents - c.remainingAmountInBaseCents;
    }
    return { issued, remaining, applied };
  }, [rows]);

  const dealersById = useMemo(
    () => new Map(dealers.map((d) => [d._id, d])),
    [dealers],
  );
  const invoicesById = useMemo(
    () => new Map(invoices.map((i) => [i._id, i])),
    [invoices],
  );
  const dealerOptions = useMemo(
    () => dealers.map((d) => ({ id: d._id, displayName: d.displayName })),
    [dealers],
  );
  const invoiceOptions = useMemo(
    () =>
      invoices.map((i) => ({
        id: i._id,
        invoiceNumber: i.invoiceNumber,
        dealerId: i.dealerId,
      })),
    [invoices],
  );

  const columns = useMemo<ColumnDef<MockCreditNote>[]>(
    () => [
      {
        accessorKey: "creditNoteNumber",
        header: "CN #",
        cell: ({ row }) => (
          <span className="font-mono text-[12px]">
            {row.original.creditNoteNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "dealer",
        header: "Dealer",
        cell: ({ row }) => (
          <span className="text-(--text-primary)">
            {dealersById.get(row.original.dealerId)?.displayName ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "creditReason",
        header: "Reason",
        cell: ({ row }) => (
          <Badge variant="neutral">
            {REASON_LABELS[row.original.creditReason] ?? row.original.creditReason}
          </Badge>
        ),
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {formatMoney(row.original.amountCents, row.original.currency, {
              locale: "en-BD",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        id: "remaining",
        header: "Remaining",
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {formatMoney(
              row.original.remainingAmountCents,
              row.original.currency,
              { locale: "en-BD" },
            )}
          </span>
        ),
        meta: { numeric: true, mono: true, hideOnMobile: true },
      },
      {
        accessorKey: "issueDate",
        header: "Issued",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDate(row.original.issueDate)}
          </span>
        ),
      },
      {
        accessorKey: "expiryDate",
        header: "Expiry",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {row.original.expiryDate
              ? formatDate(row.original.expiryDate)
              : "—"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill variant={STATUS_VARIANTS[row.original.status]}>
            {STATUS_LABELS[row.original.status]}
          </StatusPill>
        ),
      },
    ],
    [dealersById],
  );

  function handleCreate(values: CreditNoteFormValues) {
    if (!isMock) return;
    const cnPrefix =
      companyProfile.documentPrefixes?.creditNote ?? DEFAULT_PREFIXES.creditNote;
    const existingNumbers = rows
      .map((r) => r.creditNoteNumber)
      .filter((n) => n.length > 0);
    const number = generateNumber(
      "creditNote",
      existingNumbers,
      cnPrefix,
      new Date(values.issueDate).getFullYear(),
    );
    const newRow: MockCreditNote = {
      _id: `cn_local_${Date.now()}`,
      creditNoteNumber: number,
      dealerId: values.dealerId,
      originalInvoiceId: values.originalInvoiceId,
      creditReason: values.creditReason,
      currency: values.currency.toUpperCase(),
      amountCents: values.amountCents,
      fxRateToBaseE8: 100_000_000n,
      amountInBaseCents: values.amountCents,
      remainingAmountCents: values.amountCents,
      remainingAmountInBaseCents: values.amountCents,
      status: "issued",
      issueDate: new Date(values.issueDate).getTime(),
      expiryDate: values.expiryDate
        ? new Date(values.expiryDate).getTime()
        : null,
      notes: values.notes || null,
    };
    setRows((prev) => [newRow, ...prev]);
    setCreateOpen(false);
    toast.success(`Credit note ${number} created (mock)`);
  }

  function handleVoid(cn: MockCreditNote) {
    setRows((prev) =>
      prev.map((r) =>
        r._id === cn._id ? { ...r, status: "void" as const } : r,
      ),
    );
    setDetailRow(null);
    toast.success("Credit note voided (mock)");
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockCreditNote>[] = [
      { key: "creditNoteNumber", label: "CN #" },
      {
        key: "dealerId",
        label: "Dealer",
        value: (r) => dealersById.get(r.dealerId)?.displayName ?? r.dealerId,
      },
      {
        key: "creditReason",
        label: "Reason",
        format: (v) => REASON_LABELS[String(v)] ?? String(v),
      },
      { key: "amountCents", label: "Amount" },
      { key: "remainingAmountCents", label: "Remaining" },
      { key: "currency", label: "Currency" },
      {
        key: "issueDate",
        label: "Issue date",
        format: (v) => formatDateForCsv(Number(v)),
      },
      {
        key: "expiryDate",
        label: "Expiry",
        format: (v) => formatDateForCsv(v as number | null | undefined),
      },
      {
        key: "status",
        label: "Status",
        format: (v) => STATUS_LABELS[v as MockCreditNote["status"]] ?? String(v),
      },
    ];
    downloadCsv(
      exportFilename("credit-notes", extension),
      toCsv(rows, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Credit notes"
        description="Returns, write-offs, and goodwill credits owed back to dealers."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New credit note
            </Button>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        aria-label="Credit notes summary"
      >
        <KpiCard
          caption="Total issued"
          value={formatMoneySmart(kpis.issued, "BDT", { locale: "en-BD" })}
          hint="Lifetime credit issued"
        />
        <KpiCard
          caption="Remaining"
          value={formatMoneySmart(kpis.remaining, "BDT", { locale: "en-BD" })}
          hint="Available to apply"
        />
        <KpiCard
          caption="Applied"
          value={formatMoneySmart(kpis.applied, "BDT", { locale: "en-BD" })}
          hint="Already used against invoices"
        />
      </section>

      <DataTable
        data={rows}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No credit notes"
        emptyDescription="Create a credit note for a return or goodwill discount."
        emptyIcon={<Receipt className="h-5 w-5" aria-hidden />}
        emptyAction={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> New credit note
          </Button>
        }
      />

      {/* Create */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New credit note</SheetTitle>
            <SheetDescription>
              Linking to an invoice is optional but recommended for returns.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <CreditNoteForm
              dealers={dealerOptions}
              invoices={invoiceOptions}
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(o) => {
          if (!o) setDetailRow(null);
        }}
      >
        <SheetContent side="right" size="md">
          {detailRow ? (
            <CreditNoteDetail
              row={detailRow}
              onVoid={() => handleVoid(detailRow)}
              dealersById={dealersById}
              invoicesById={invoicesById}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CreditNoteDetail({
  row,
  onVoid,
  dealersById,
  invoicesById,
}: {
  row: MockCreditNote;
  onVoid: () => void;
  dealersById: Map<string, MockDealerRecord>;
  invoicesById: Map<string, MockInvoice>;
}) {
  const dealer = dealersById.get(row.dealerId) ?? null;
  const linkedInvoice = row.originalInvoiceId
    ? (invoicesById.get(row.originalInvoiceId) ?? null)
    : null;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span className="font-mono">{row.creditNoteNumber}</span>
          <StatusPill variant={STATUS_VARIANTS[row.status]}>
            {STATUS_LABELS[row.status]}
          </StatusPill>
        </SheetTitle>
        <SheetDescription>{dealer?.displayName ?? "—"}</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          <section className="grid grid-cols-2 gap-3">
            <DetailField
              label="Amount"
              value={formatMoney(row.amountCents, row.currency, {
                locale: "en-BD",
                display: "code",
              })}
            />
            <DetailField
              label="Remaining"
              value={formatMoney(row.remainingAmountCents, row.currency, {
                locale: "en-BD",
                display: "code",
              })}
            />
            <DetailField
              label="Reason"
              value={REASON_LABELS[row.creditReason] ?? row.creditReason}
            />
            <DetailField label="Issued" value={formatDate(row.issueDate)} />
            <DetailField
              label="Expires"
              value={
                row.expiryDate ? formatDate(row.expiryDate) : "No expiry"
              }
            />
            {linkedInvoice ? (
              <DetailField
                label="Original invoice"
                value={
                  <span className="font-mono">
                    {linkedInvoice.invoiceNumber || "(draft)"}
                  </span>
                }
              />
            ) : null}
          </section>

          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {row.notes}
              </p>
            </section>
          ) : null}
        </div>
      </SheetBody>
      <SheetFooter>
        {row.status === "issued" && row.remainingAmountCents > 0n ? (
          <Button
            onClick={() => toast.info("Apply to invoice — coming next phase")}
          >
            Apply to invoice
          </Button>
        ) : null}
        {row.status === "issued" && row.remainingAmountCents === row.amountCents ? (
          <Button variant="destructive" onClick={onVoid}>
            Void
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}
